import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface Draft {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  featured?: boolean;
  status: 'draft' | 'scheduled' | 'published';
  type: 'blog' | 'portfolio';
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  scheduledFor?: Date;
  version: number;
  versions: DraftVersion[];
}

interface DraftVersion {
  id: string;
  version: number;
  content: string;
  title: string;
  createdAt: Date;
  note?: string;
}

interface DraftManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (draft: Draft) => void;
  onPreview: (draft: Draft) => void;
  type: 'blog' | 'portfolio';
}

const DraftManager: React.FC<DraftManagerProps> = ({
  isOpen,
  onClose,
  onEdit,
  onPreview,
  type,
}) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [filter, setFilter] = useState<'all' | 'draft' | 'scheduled' | 'published'>('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDrafts();
    }
  }, [isOpen, type]);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      // Load drafts from localStorage for now (in production, this would be from API)
      const savedDrafts = localStorage.getItem(`drafts_${type}`);
      if (savedDrafts) {
        const parsedDrafts = JSON.parse(savedDrafts).map((draft: any) => ({
          ...draft,
          createdAt: new Date(draft.createdAt),
          updatedAt: new Date(draft.updatedAt),
          publishedAt: draft.publishedAt ? new Date(draft.publishedAt) : undefined,
          scheduledFor: draft.scheduledFor ? new Date(draft.scheduledFor) : undefined,
          versions: draft.versions?.map((v: any) => ({
            ...v,
            createdAt: new Date(v.createdAt),
          })) || [],
        }));
        setDrafts(parsedDrafts);
      }
    } catch (error) {
      console.error('Error loading drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  // saveDraft function removed as it's handled in parent component

  const deleteDraft = (draftId: string) => {
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    setDrafts(updatedDrafts);
    localStorage.setItem(`drafts_${type}`, JSON.stringify(updatedDrafts));
    setSelectedDraft(null);
  };

  const publishDraft = async (draft: Draft) => {
    try {
      // In production, this would make an API call to publish the content
      const publishedDraft = {
        ...draft,
        status: 'published' as const,
        publishedAt: new Date(),
      };
      
      const updatedDrafts = drafts.map(d => d.id === draft.id ? publishedDraft : d);
      setDrafts(updatedDrafts);
      localStorage.setItem(`drafts_${type}`, JSON.stringify(updatedDrafts));
      
      // Here you would also save to the main content API
      console.log('Publishing draft:', publishedDraft);
    } catch (error) {
      console.error('Error publishing draft:', error);
    }
  };

  const scheduleDraft = (draft: Draft, scheduledFor: Date) => {
    const scheduledDraft = {
      ...draft,
      status: 'scheduled' as const,
      scheduledFor,
    };
    
    const updatedDrafts = drafts.map(d => d.id === draft.id ? scheduledDraft : d);
    setDrafts(updatedDrafts);
    localStorage.setItem(`drafts_${type}`, JSON.stringify(updatedDrafts));
  };

  const restoreVersion = (draft: Draft, version: DraftVersion) => {
    const restoredDraft = {
      ...draft,
      title: version.title,
      content: version.content,
      updatedAt: new Date(),
      version: draft.version + 1,
    };
    
    const updatedDrafts = drafts.map(d => d.id === draft.id ? restoredDraft : d);
    setDrafts(updatedDrafts);
    localStorage.setItem(`drafts_${type}`, JSON.stringify(updatedDrafts));
    setSelectedDraft(restoredDraft);
  };

  const filteredDrafts = drafts.filter(draft => {
    if (filter === 'all') return true;
    return draft.status === filter;
  });

  const getStatusIcon = (status: Draft['status']) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="w-4 h-4 text-gray-500" />;
      case 'scheduled':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />;
      case 'published':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
  };

  const getStatusColor = (status: Draft['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'published':
        return 'bg-green-100 text-green-800';
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DocumentIcon className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Draft Manager - {type === 'blog' ? 'Blog Posts' : 'Portfolio Items'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ×
          </button>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Sidebar - Draft List */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            {/* Filter Tabs */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {(['all', 'draft', 'scheduled', 'published'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      filter === status
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Draft List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading drafts...</div>
              ) : filteredDrafts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No {filter === 'all' ? '' : filter} drafts found
                </div>
              ) : (
                filteredDrafts.map((draft) => (
                  <div
                    key={draft.id}
                    onClick={() => setSelectedDraft(draft)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                      selectedDraft?.id === draft.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-gray-900 truncate">{draft.title}</h3>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(draft.status)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(draft.status)}`}>
                        {draft.status}
                      </span>
                      <span className="text-xs text-gray-500">v{draft.version}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Updated {format(draft.updatedAt, 'MMM d, yyyy')}
                    </p>
                    {draft.scheduledFor && (
                      <p className="text-xs text-yellow-600">
                        Scheduled for {format(draft.scheduledFor, 'MMM d, yyyy HH:mm')}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {selectedDraft ? (
              <>
                {/* Draft Details Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {selectedDraft.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Version {selectedDraft.version}</span>
                        <span>Created {format(selectedDraft.createdAt, 'MMM d, yyyy')}</span>
                        <span>Updated {format(selectedDraft.updatedAt, 'MMM d, yyyy HH:mm')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onPreview(selectedDraft)}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                      >
                        <EyeIcon className="w-4 h-4" />
                        Preview
                      </button>
                      <button
                        onClick={() => onEdit(selectedDraft)}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {selectedDraft.status === 'draft' && (
                      <>
                        <button
                          onClick={() => publishDraft(selectedDraft)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Publish Now
                        </button>
                        <button
                          onClick={() => {
                            const date = prompt('Schedule for (YYYY-MM-DD HH:MM):');
                            if (date) {
                              scheduleDraft(selectedDraft, new Date(date));
                            }
                          }}
                          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
                        >
                          <CalendarIcon className="w-4 h-4" />
                          Schedule
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setShowVersions(!showVersions)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Versions ({selectedDraft.versions.length})
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this draft?')) {
                          deleteDraft(selectedDraft.id);
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {showVersions ? (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Version History</h4>
                      <div className="space-y-4">
                        {selectedDraft.versions.map((version) => (
                          <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-medium text-gray-900">Version {version.version}</span>
                                <span className="text-sm text-gray-600 ml-2">
                                  {format(version.createdAt, 'MMM d, yyyy HH:mm')}
                                </span>
                              </div>
                              <button
                                onClick={() => restoreVersion(selectedDraft, version)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                              >
                                Restore
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{version.note}</p>
                            <h5 className="font-medium text-gray-900 mb-1">{version.title}</h5>
                            <div className="text-sm text-gray-600 max-h-20 overflow-hidden">
                              <div dangerouslySetInnerHTML={{ __html: version.content.substring(0, 200) + '...' }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Content Preview</h4>
                      <div className="prose max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: selectedDraft.content }} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <DocumentIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a draft to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DraftManager;